'use client';

import { useCallback } from 'react';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useCalculateMatingPredictionLazyQuery } from '@/publicSearch/graphql/queries.generated';
import type {
  MatingPredictorResponse,
  MatingSearchFormValues,
} from '@/components/publicSearch/mating/types';
import {
  hasWildcardChars,
  normalizeWildcardInput,
} from '@/components/publicSearch/search/animalIdentChipFilter';

function matingIdentForApi(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (hasWildcardChars(t)) return normalizeWildcardInput(t);
  return t;
}

/**
 * Hook that calls the mating predictor via the GraphQL API (CalculateMatingPrediction query).
 * Uses the public-search Apollo client with X-Society-Id header from context.
 */
export function useMatingPredictor() {
  const orgId = usePublicSearchContext((context) => context.society.orgId);

  const [runQuery, { data: queryData, loading, error }] =
    useCalculateMatingPredictionLazyQuery();

  const calculate = useCallback(
    async (
      formValues: MatingSearchFormValues,
    ): Promise<MatingPredictorResponse> => {
      const sireIdents = formValues.sireIdents
        .split(',')
        .map(matingIdentForApi)
        .filter(Boolean);

      const damIdents = formValues.damIdents
        .split(',')
        .map(matingIdentForApi)
        .filter(Boolean);

      const requestInput = {
        sireIdents,
        damIdents,
        damCalvingYears: formValues.damCalvingYears || null,
        maxAnimals: formValues.maxAnimals,
        displayBy: formValues.displayBy,
        maxGenerations: 10,
        includeInbreeding: true,
        // TODO: make this a society setting (SocietySettingType.MatingInbreedingYears) that
        // coalesces to 50 on the backend, then remove this field from MatingPredictorRequestInput
        // entirely so the frontend doesn't need to know about it.
        inbreedingYears: 50,
      };

      const result = await runQuery({
        variables: { requestInput },
        context: { headers: setDefaultHeader(orgId) },
      });

      if (result.error) {
        throw result.error;
      }

      const response = result.data?.calculateMatingPrediction;
      if (!response) {
        throw new Error('No data returned from mating predictor');
      }

      return response as MatingPredictorResponse;
    },
    [orgId, runQuery],
  );

  const data = (queryData?.calculateMatingPrediction ??
    null) as MatingPredictorResponse | null;

  return {
    calculate,
    data,
    loading,
    error: error ?? null,
  };
}
